import React from 'react';
import { AssignmentReturn, Close, SwapHoriz } from '@mui/icons-material';
import { Avatar, Box, CircularProgress, Typography } from '@mui/material';

export default function DashboardReturnSearchPanel({
  billingMode,
  returnSearchResults,
  returnOrderMatches,
  returnSearched,
  selectedReturnCustomer,
  isSearchingCustomers,
  isSelectingOrder,
  handleSelectExchangeOrder,
  handleSelectReturnOrder,
  handleSelectReturnCustomer,
  setSelectedReturnCustomer,
  setReturnCustomerOrders,
  setReturnOrderInput,
  setReturnSearchResults,
  setReturnOrderMatches,
  setReturnSearched,
  setLookedUpOrder,
  lookedUpOrder,
  returnDrawerOpenDash,
  setReturnDrawerOpenDash,
  returnCustomerOrders,
  isLoadingCustomerOrders,
}) {
  const isExchange = billingMode === 'exchange';
  const accentColor = isExchange ? '#6366F1' : '#C2410C';
  const accentBg = isExchange ? '#F5F3FF' : '#FFFAF5';
  const accentBorder = isExchange ? '#C7D2FE' : '#FDBA74';
  const accentHoverBg = isExchange ? '#EEF2FF' : '#FFF7ED';
  const accentHoverBorder = isExchange ? '#A5B4FC' : '#FB923C';
  const handleSelectOrder = isExchange ? handleSelectExchangeOrder : handleSelectReturnOrder;
  const hasResults = returnSearchResults.length > 0 || returnOrderMatches.length > 0;

  return (
    <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', p: 2 }}>
      {(returnSearched || selectedReturnCustomer) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Typography
            fontSize="0.72rem"
            fontWeight={700}
            color="#94A3B8"
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.4, '&:hover': { color: '#EF4444' } }}
            onClick={() => {
              setReturnOrderInput('');
              setReturnSearchResults([]);
              setReturnOrderMatches([]);
              setReturnSearched(false);
              setSelectedReturnCustomer(null);
              setReturnCustomerOrders([]);
              setLookedUpOrder(null);
            }}
          >
            <Close sx={{ fontSize: 12 }} /> Clear search
          </Typography>
        </Box>
      )}

      {!returnSearched && !isSearchingCustomers && !selectedReturnCustomer && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, gap: 1.5, pb: 4 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: isExchange ? '#EEF2FF' : '#FFF7ED', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isExchange
              ? <SwapHoriz sx={{ fontSize: 26, color: accentColor }} />
              : <AssignmentReturn sx={{ fontSize: 24, color: accentColor }} />}
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography fontWeight={800} fontSize="0.9rem" color="#1E293B" sx={{ mb: 0.4 }}>
              {isExchange ? 'Exchange Mode' : 'Return Mode'}
            </Typography>
            <Typography fontSize="0.75rem" color="#94A3B8">Search by invoice number or customer name</Typography>
          </Box>
        </Box>
      )}

      {isSearchingCustomers && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress size={22} sx={{ color: accentColor }} />
        </Box>
      )}

      {returnSearched && !isSearchingCustomers && !selectedReturnCustomer && !hasResults && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, gap: 1, pb: 4 }}>
          <Typography fontSize="0.82rem" fontWeight={700} color="#64748B">No results found</Typography>
          <Typography fontSize="0.74rem" color="#94A3B8">Try a different name, phone, or invoice number</Typography>
        </Box>
      )}

      {!selectedReturnCustomer && returnOrderMatches.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: returnSearchResults.length > 0 ? 2 : 0 }}>
          <Typography fontSize="0.68rem" color="#94A3B8" fontWeight={700} sx={{ mb: 0.25 }}>INVOICE MATCH</Typography>
          {returnOrderMatches.map((order) => (
            <Box
              key={order.id}
              onClick={() => !isSelectingOrder && handleSelectOrder(order)}
              sx={{ p: 1.5, borderRadius: '10px', border: `1px solid ${accentBorder}`, bgcolor: accentBg, cursor: isSelectingOrder ? 'wait' : 'pointer', '&:hover': { bgcolor: accentHoverBg, borderColor: accentHoverBorder } }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                <Typography fontSize="0.84rem" fontWeight={800} color={accentColor}>{order.order_number}</Typography>
                <Typography fontSize="0.82rem" fontWeight={700} color="#1E293B">₹{parseFloat(order.grand_total || 0).toFixed(2)}</Typography>
              </Box>
              <Typography fontSize="0.71rem" color="#64748B">
                {order.customer_name || 'Walk-in'} · {order.items_count} item{order.items_count !== 1 ? 's' : ''} · {order.paid_at ? new Date(order.paid_at).toLocaleDateString('en-IN', { dateStyle: 'short' }) : ''}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {!selectedReturnCustomer && returnSearchResults.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography fontSize="0.68rem" color="#94A3B8" fontWeight={700} sx={{ mb: 0.25 }}>SELECT CUSTOMER</Typography>
          {returnSearchResults.map((c) => (
            <Box
              key={c.id}
              onClick={() => handleSelectReturnCustomer(c)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, borderRadius: '10px', border: '1px solid #E2E8F0', bgcolor: '#fff', cursor: 'pointer', '&:hover': { bgcolor: '#F5F3FF', borderColor: '#A5B4FC' } }}
            >
              <Avatar sx={{ width: 34, height: 34, bgcolor: '#EEF2FF', color: '#6366F1', fontSize: '0.78rem', fontWeight: 800 }}>
                {c.name?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontSize="0.84rem" fontWeight={700} color="#1E293B" noWrap>{c.name}</Typography>
                <Typography fontSize="0.72rem" color="#94A3B8">{c.phone_number}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {selectedReturnCustomer && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              fontSize="0.76rem"
              fontWeight={700}
              color="#6366F1"
              sx={{ cursor: 'pointer', flexShrink: 0 }}
              onClick={() => {
                setSelectedReturnCustomer(null);
                setReturnCustomerOrders([]);
              }}
            >
              ← Back
            </Typography>
            <Typography fontSize="0.72rem" color="#64748B" fontWeight={600} noWrap>
              {selectedReturnCustomer.name}'s paid orders
            </Typography>
          </Box>
          {isLoadingCustomerOrders && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} sx={{ color: accentColor }} />
            </Box>
          )}
          {!isLoadingCustomerOrders && returnCustomerOrders.length === 0 && (
            <Typography fontSize="0.78rem" color="#94A3B8" textAlign="center" sx={{ py: 2 }}>No paid orders found</Typography>
          )}
          {returnCustomerOrders.map((order) => (
            <Box
              key={order.id}
              onClick={() => !isSelectingOrder && handleSelectOrder(order)}
              sx={{ p: 1.5, borderRadius: '10px', border: `1px solid ${accentBorder}`, bgcolor: accentBg, cursor: isSelectingOrder ? 'wait' : 'pointer', '&:hover': { bgcolor: accentHoverBg, borderColor: accentHoverBorder } }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                <Typography fontSize="0.84rem" fontWeight={800} color={accentColor}>{order.order_number}</Typography>
                <Typography fontSize="0.82rem" fontWeight={700} color="#1E293B">₹{parseFloat(order.grand_total || 0).toFixed(2)}</Typography>
              </Box>
              <Typography fontSize="0.71rem" color="#64748B">
                {order.items_count} item{order.items_count !== 1 ? 's' : ''} · {order.paid_at ? new Date(order.paid_at).toLocaleDateString('en-IN', { dateStyle: 'short' }) : ''}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {!isExchange && lookedUpOrder && !returnDrawerOpenDash && !selectedReturnCustomer && (
        <Box
          onClick={() => setReturnDrawerOpenDash(true)}
          sx={{ mt: 'auto', border: `1px solid ${accentBorder}`, borderRadius: '10px', p: 1.5, bgcolor: accentBg, cursor: 'pointer', '&:hover': { bgcolor: accentHoverBg } }}
        >
          <Typography fontSize="0.66rem" color="#94A3B8" fontWeight={700} sx={{ mb: 0.3 }}>LAST OPENED</Typography>
          <Typography fontWeight={800} fontSize="0.86rem" color={accentColor}>{lookedUpOrder.order_number}</Typography>
          <Typography fontSize="0.72rem" color="#64748B">{lookedUpOrder.customer_name || 'Walk-in'} · ₹{parseFloat(lookedUpOrder.grand_total || 0).toFixed(2)}</Typography>
          <Typography fontSize="0.65rem" color="#94A3B8" sx={{ mt: 0.3 }}>Tap to reopen return drawer</Typography>
        </Box>
      )}
    </Box>
  );
}
